import { useUser } from "@clerk/clerk-react";
import type {
  GetTokenOptions,
  SignedInSessionResource,
  UserResource,
} from "@clerk/types";
import { CheckIcon, MinusIcon } from "@heroicons/react/20/solid";
import * as Sentry from "@sentry/react";
import type { SupabaseClient } from "@supabase/supabase-js";
import axios from "axios";
import { isArray, isEqual, isObject, mapValues } from "lodash";
import pako from "pako";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { PRD_CONSTANTS } from "@/components/AI/PRD/constants";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { SubMenuApplicationStatus } from "@/store/zustand/commandPaletteStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import {
  usePagesStore,
  useStoryboardsForPage,
} from "@/store/zustand/pagesStore";
import { usePMAgentStore } from "@/store/zustand/pmAgentStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import type { Issue } from "@/types/issues";
import type { User } from "@/types/users";
import { originalConsoleError } from "./events/tracker";
import type { Canvas, NavTreeNode, SubCanvas } from "./types";

export const DELETE_STYLE_CONSTANT = null;
export const DELETE_PROP_CONSTANT = null;

export const STORYBOARDS_FOLDER = "tempobook/storyboards";
export const DYNAMIC_FOLDER = "tempobook/dynamic";

export const ADMIN_USER_IDS = ["github|4885048"];

export const isMacOS = () => {
  if (typeof navigator === "undefined") return true; // Default to Mac in SSR
  return /Mac/i.test(navigator.userAgent);
};

// Helper function to get platform-specific modifier key
export const getModifierKey = () => {
  return isMacOS() ? "⌘" : "Ctrl";
};

// Helper function to get platform-specific alt key
export const getAltKey = () => {
  return isMacOS() ? "⌥" : "Alt";
};

// Helper function to get platform-specific shift key
export const getShiftKey = () => {
  return isMacOS() ? "⇧" : "Shift";
};

export const useUserId = () => {
  const { user } = useUser();

  return user?.externalId;
};

export const rgba2hex = (rgba) => {
  if (!rgba || rgba === "") {
    return rgba;
  }

  try {
    // Taken from https://stackoverflow.com/questions/1740700/how-to-get-hex-color-value-rather-than-rgb-value
    return `#${rgba
      .match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+\.{0,1}\d*))?\)$/)
      .slice(1)
      .map((n, i) =>
        (i === 3 ? Math.round(parseFloat(n) * 255) : parseFloat(n))
          .toString(16)
          .padStart(2, "0")
          .replace("NaN", ""),
      )
      .join("")}`;
  } catch {
    return rgba;
  }
};

export const camelCaseToTitle = (camelCase) => {
  // Replace each lowercase to uppercase transition with a space and the uppercase letter
  let result = camelCase.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Capitalize the first letter of the result if it's lowercase
  result = result.charAt(0).toUpperCase() + result.slice(1);

  return result;
};

export const convertSnakeToCamelCase = (st: string): string => {
  return st.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

export const convertCamelToSnakeCase = (str: string) => {
  if (!str) return str;

  return (
    str.charAt(0).toLowerCase() +
    str.substring(1).replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
  );
};

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]; // Create a copy of the original array to avoid modifying it directly
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
  }
  return shuffled;
}

export const kebabize = (str: string) =>
  str.replace(
    /[A-Z]+(?![a-z])|[A-Z]/g,
    ($, ofs) => (ofs ? "-" : "") + $.toLowerCase(),
  );

/**
 * From https://usehooks.com/usePrevious/
 */
export const usePrevious = (value) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

export const useTraceUpdate = (props) => {
  const prev = useRef(props);
  useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.debug("Changed props:", changedProps);
    }
    prev.current = props;
  });
};

// We want to make event listeners non-passive, and to do so have to check
// that browsers support EventListenerOptions in the first place.
// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
let passiveSupported = false;

export const makePassiveEventOption = () => {
  try {
    const options = {
      get passive() {
        // This function will be called when the browser
        //   attempts to access the passive property.
        passiveSupported = true;
        return false;
      },
    };

    return options;
  } catch (err) {
    passiveSupported = false;
    return passiveSupported;
  }
};

export const nearestPowerOf2 = (n) => {
  return 1 << (31 - Math.clz32(n));
};

export const storyboardsOverlap = (storyboard1: any, storyboard2: any) => {
  // Check if one storyboard is on the left side of the other
  if (
    storyboard1.x + storyboard1.width < storyboard2.x ||
    storyboard2.x + storyboard2.width < storyboard1.x
  ) {
    return false;
  }

  // Check if one storyboard is above the other
  if (
    storyboard1.y + storyboard1.height < storyboard2.y ||
    storyboard2.y + storyboard2.height < storyboard1.y
  ) {
    return false;
  }

  // If neither of the conditions above is met, storyboards overlap
  return true;
};

export const storyboardOverlapsWithAnyOthers = (
  storyboard: any,
  allStoryboards: any[],
) => {
  if (!allStoryboards || !storyboard) {
    return false;
  }

  return allStoryboards?.some((otherStoryboard) =>
    storyboardsOverlap(storyboard, otherStoryboard),
  );
};

/**
 * Returns the x & y position of the top left corner of the storyboard so that it doens't
 * overlap with any other storyboards
 */
export const getNewStoryboardPosition = (
  width: number,
  height: number,
  allStoryboards: any[],
) => {
  // Try to insert in the middle but then move right
  let insertX = -width / 2;
  const insertY = -height / 2;

  let hasOverlap = true;
  while (hasOverlap) {
    hasOverlap = false;

    allStoryboards
      .filter((storyboard) => storyboard.deleted !== true)
      .forEach((storyboard) => {
        if (
          storyboardsOverlap(storyboard, {
            x: insertX - 79,
            y: insertY - 79,
            width: width + 158,
            height: height + 158,
          })
        ) {
          hasOverlap = true;
          const newInsertX = storyboard.x + storyboard.width + 80;
          if (newInsertX > insertX) {
            insertX = newInsertX;
          }
        }
      });
  }

  return {
    x: insertX,
    y: insertY,
  };
};

export const getAxiosErorrMessage = (error: any) => {
  let errorMessage;

  if (error?.response) {
    errorMessage = error?.response?.data?.message || error?.response?.data;
  } else if (error?.message) {
    errorMessage = error?.message;
  } else {
    errorMessage = error;
  }

  // Try to parse the error as a JSON object
  try {
    errorMessage = JSON.parse(errorMessage);
  } catch (e) {
    // Do nothing
  }

  if (typeof errorMessage === "object") {
    if (errorMessage["error"]) {
      errorMessage = errorMessage["error"];
    }

    if (typeof errorMessage === "object") {
      try {
        errorMessage = JSON.stringify(errorMessage);
      } catch (e) {
        // Do nothing
      }
    }
  }

  return errorMessage;
};

export const isDemoUser = (user: UserResource | null | undefined) => {
  if (!user) {
    return false;
  }

  const userId = user.externalId;

  return (
    userId === "auth0|649b51281bbdccdf60b79219" ||
    (user.primaryEmailAddress?.emailAddress?.includes("demo") &&
      (user.primaryEmailAddress?.emailAddress?.endsWith("buildtempo.dev") ||
        user.primaryEmailAddress?.emailAddress?.endsWith("tempolabs.ai") ||
        user.primaryEmailAddress?.emailAddress?.endsWith("tempo.new")))
  );
};

export async function fetchUsers(
  projectId: string,
  session: SignedInSessionResource | null,
) {
  if (!session) {
    return;
  }

  const supabaseJWT = await getUnexpiredToken(session);
  if (!supabaseJWT) {
    console.error("Failed to get Supabase JWT, cannot fetch users.");
    return;
  }

  const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_BACKEND_BASE_URL,
    headers: {
      Authorization: `Bearer ${supabaseJWT}`,
    },
  });

  try {
    const usersResp = await axiosInstance.get(`/projects/${projectId}/users`);
    const apiUsers: User[] =
      usersResp?.data?.users?.map(
        (user: any): User => ({
          name: user.name,
          email: user.email,
          id: user.auth0_user_id,
          avatarUrl: user.picture,
        }),
      ) || [];

    return apiUsers;
  } catch (e) {
    console.error(`Error fetching users for project ${projectId}:`, e);
  }
}

export const fetchProjectIdFromCanvasId = async (
  canvasId: string,
  supabaseClient: SupabaseClient,
) => {
  if (!supabaseClient) {
    return null;
  }

  const projectId = await supabaseClient
    .from("canvases")
    .select("project_id")
    .eq("id", canvasId)
    .single()
    .then((response) => {
      return response.data?.project_id;
    });

  return projectId;
};

export const fetchProjectById = async (
  projectId: string,
  supabaseClient: SupabaseClient,
) => {
  const project = await supabaseClient
    .from("projects")
    .select("*")
    .eq("id", projectId)
    .single();
  return project;
};

export const useActiveMainCanvas = (): Canvas => {
  const canvasId = useCanvasId();
  const mainCanvas = useIssuesStore((state) => state.currentCanvas[canvasId]);
  const selectedPageId = usePagesStore((state) => state.selectedPageId);
  const currentPageId = mainCanvas?.id ? selectedPageId[mainCanvas.id] : null;
  const hasPages = usePagesStore(
    (state) => mainCanvas?.id && state.pages[mainCanvas.id]?.length > 0,
  );
  const pageStoryboards = useStoryboardsForPage(mainCanvas?.id, currentPageId);

  return useMemo(() => {
    if (mainCanvas?.id && hasPages) {
      return { ...mainCanvas, storyboards: pageStoryboards };
    }
    return mainCanvas;
  }, [mainCanvas, hasPages, pageStoryboards]);
};

export const useDevServerSuffix = (): string | null => {
  const canvasId = useCanvasId();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const [serverSuffix, setServerSuffix] = useState<string | null>(null);

  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    const fetchServerSuffix = async () => {
      try {
        const { data } = await supabaseClient
          .from("canvas_machine_mapping")
          .select("machine_id")
          .eq("canvas_id", canvasId)
          .maybeSingle();

        if (!data) {
          return;
        }

        const { data: machine } = await supabaseClient
          .from("deployment_machines")
          .select("new_dev_server_suffix,is_new_active,dev_server_suffix")
          .eq("id", data.machine_id)
          .single();

        const devServerSuffix = machine?.is_new_active
          ? machine?.new_dev_server_suffix
          : machine?.dev_server_suffix;

        if (!devServerSuffix) {
          return;
        }

        setServerSuffix(devServerSuffix);
      } catch (e) {
        console.error(e);
      }
    };

    fetchServerSuffix();
  }, [canvasId, supabaseClient]);

  return serverSuffix;
};

export const useActiveProject = (): any => {
  const mainCanvas = useActiveMainCanvas();
  const projectId = useProjectId();

  const projects = useSelector((state: any) => state.supabaseStore.projects);

  return useMemo(() => {
    const projectIdToUse = mainCanvas?.project_id || projectId;

    if (!projectIdToUse) {
      return null;
    }

    return projects?.[projectIdToUse];
  }, [mainCanvas?.project_id, projectId, projects]);
};

export const useActiveCanvas = (): Canvas | SubCanvas | null => {
  const mainCanvas = useActiveMainCanvas();
  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );
  const subCanvases = useSupabaseStore((state) => state.subCanvases);
  const selectedPageId = usePagesStore((state) => state.selectedPageId);
  const hasPages = usePagesStore(
    (state) => mainCanvas?.id && state.pages[mainCanvas.id]?.length > 0,
  );
  const currentPageId = mainCanvas?.id ? selectedPageId[mainCanvas.id] : null;
  const pageStoryboards = useStoryboardsForPage(mainCanvas?.id, currentPageId);

  return useMemo(() => {
    if (!canvasComponentKey) {
      // Not in a subcanvas - check if we should use page storyboards
      if (mainCanvas?.id && hasPages) {
        return { ...mainCanvas, storyboards: pageStoryboards };
      }
      return mainCanvas;
    }

    return Object.values(subCanvases).find(
      (subCanvas: SubCanvas) =>
        subCanvas.canvas_id === mainCanvas.id &&
        subCanvas.component_id === canvasComponentKey,
    ) as SubCanvas;
  }, [mainCanvas, canvasComponentKey, subCanvases, hasPages, pageStoryboards]);
};

export const getDevServerUrl = (canvas: Canvas) => {
  if (!canvas) {
    return "";
  }

  if (import.meta.env.VITE_ENVIRONMENT === "DEV" && !canvas?.assigned_port) {
    return "";
  }

  // Kubernetes mode: Use canvas ID-based URL pattern
  // In local mode, tempo-preview.localhost ensures the iframes are on a different thread
  return import.meta.env.VITE_ENVIRONMENT === "DEV"
    ? `http://tempo-preview.localhost:${canvas.assigned_port}`
    : `https://${canvas.id}.${import.meta.env.VITE_CANVAS_DOMAIN}`;
};

// Taken from https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
// Must match hash string on backend
export const hashString = (str: string, seed: number = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

/**
 * Memoize a result using deep equality. This hook has two advantages over
 * React.useMemo: it uses deep equality to compare memo keys, and it guarantees
 * that the memo function will only be called if the keys are unequal.
 * React.useMemo cannot be relied on to do this, since it is only a performance
 * optimization (see https://reactjs.org/docs/hooks-reference.html#usememo).
 */
export function useDeepMemo<TKey, TValue>(
  memoFn: () => TValue,
  key: TKey,
): TValue {
  const ref = useRef<{ key: TKey; value: TValue }>();

  if (!ref.current || !isEqual(key, ref.current.key)) {
    ref.current = { key, value: memoFn() };
  }

  return ref.current.value;
}

// Taken from: https://developer.mozilla.org/en-US/docs/Glossary/Void_element
const voidElements = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

export const canDragIntoNavNode = (navNode: NavTreeNode) => {
  // If there is no tag assume it's ok to drag into (just a virtual component)
  return (
    !navNode.elementTagName ||
    !voidElements.has(navNode.elementTagName.toLowerCase())
  );
};

export const isNullOrUndefined = (val: any) => {
  return val === null || val === undefined;
};

export const imageStoryboardTemplateNextJs = (
  pathToImage: string,
  naturalWidth: number,
  naturalHeight: number,
  altText: string,
) => `
import Image from 'next/image';

const PastedImage = () => {
  return (
    <Image
      src="${pathToImage}"
      alt="${altText}"
      width={${naturalWidth}}
      height={${naturalHeight}}
      className="w-full h-full"
    />
  );
};

export default PastedImage;

`;

export const imageStoryboardTemplateReact = (
  pathToImage: string,
  naturalWidth: number,
  naturalHeight: number,
  altText: string,
) => `
const PastedImage = () => {
  return (
    <img
      src="${pathToImage}"
      alt="${altText}"
      width={${naturalWidth}}
      height={${naturalHeight}}
      className="w-full h-full"
    />
  );
};

export default PastedImage;

`;

function isSerializable(value) {
  if (value === null || typeof value === "undefined") {
    return true;
  }

  const type = typeof value;
  if (type === "number" || type === "string" || type === "boolean") {
    return true;
  }

  if (type === "object") {
    if (isArray(value)) {
      return true;
    }
    if (isObject(value)) {
      return true;
    }
  }

  return false;
}

/**
 * Retuns a copy of the object with all non-serializable values removed.
 */
export function filterSerializable(obj) {
  if (!isSerializable(obj)) {
    return undefined;
  }

  if (isArray(obj)) {
    return obj
      .map(filterSerializable)
      .filter((item) => typeof item !== "undefined");
  }

  if (isObject(obj)) {
    return mapValues(obj, filterSerializable);
  }

  return obj;
}

/**
 * This function used in both the frontend and backend to fix JSON.
 *
 * A best-effort attempt at modifying the input JSON such that it represents well-formed JSON.
 * This is used to interpret partially incomplete JSON (like the intermediate result of a streamed result)
 * in the most semantically correct way.
 *
 * This can also be used to fix complete JSON in terms of invalid characters, etc
 *
 * @param jsonString
 * @returns A string rerepsenting well-formed JSON or null.
 */
export const fixJson = (
  jsonStringInput: string,
  verbose = false,
): {
  fixedStr: string;
  fixedObj: any;
  complete: boolean;
  broken: boolean;
} => {
  let jsonString = jsonStringInput.trim();

  if (!jsonString) {
    return {
      fixedStr: "{}",
      fixedObj: {},
      complete: false,
      broken: true,
    };
  }

  // If the JSON starts with some unrelated text, remove it
  // Sometimes anthropic gives back text first instead of just the JSON result
  if (
    !jsonString.startsWith("{") &&
    !jsonString.startsWith("[") &&
    (jsonString.includes("{") || jsonString.includes("["))
  ) {
    const indexOfBracket = jsonString.indexOf("{");
    const indexOfSquareBracket = jsonString.indexOf("[");

    // Find the first valid bracket (handle -1 from indexOf when not found)
    const validIndices = [indexOfBracket, indexOfSquareBracket].filter(
      (i) => i !== -1,
    );
    const sliceIndex = validIndices.length > 0 ? Math.min(...validIndices) : 0;

    jsonString = jsonString.slice(sliceIndex).trim();
  }

  // After prefix removal, strip markdown code fences if present
  // The prefix removal above slices to the first {, which is inside the code fence
  // so we only need to check for trailing ```
  if (jsonString.endsWith("```")) {
    jsonString = jsonString.slice(0, -3).trim();
  }

  const unclosedSymbols = [];
  let insideString = false;

  // Key defined with no colon after it
  let needColon = false;

  // Key defined with no value
  let needValue = false;

  // Happens when there is a trailing comma, need to remove it
  let trailingComma = false;

  // Rebuild the json as we loop for things like fixing invalid characters
  let buildNewJsonString = "";

  for (let i = 0; i < jsonString.length; i++) {
    const chr = jsonString.charAt(i);

    buildNewJsonString += chr;

    // If it's not a whitespace character, always set trailingComma to false
    // Becuase we are not ending in a comma
    if (!/\s/.test(chr)) {
      trailingComma = false;
    }

    // Inside a string
    if (insideString) {
      if (chr === '"' && jsonString.charAt(i - 1) !== "\\") {
        insideString = false;
      }

      // Need to escape these non-escaped characters
      if (chr === "\n") {
        buildNewJsonString = buildNewJsonString.slice(0, -1) + "\\n";
      }

      if (chr === "\r") {
        buildNewJsonString = buildNewJsonString.slice(0, -1) + "\\r";
      }

      if (chr === "\t") {
        buildNewJsonString = buildNewJsonString.slice(0, -1) + "\\t";
      }

      // Otherwise ignore everything else
      continue;
    }

    if (["{", "["].includes(chr)) {
      // @ts-expect-error TS2345
      unclosedSymbols.push(chr);

      // This is the start of the value
      if (needValue) {
        needValue = false;
      }

      continue;
    }

    if (["}", "]"].includes(chr)) {
      unclosedSymbols.pop();

      continue;
    }

    if (chr === '"' && jsonString.charAt(i - 1) !== "\\") {
      insideString = true;

      // This is the start of the value if in an object
      if (unclosedSymbols[unclosedSymbols.length - 1] === "{") {
        if (needValue) {
          needValue = false;
        } else {
          // This is the start of a key
          needValue = true;
          needColon = true;
        }
      }

      continue;
    }

    if (chr === ":") {
      needColon = false;
      continue;
    }

    if (chr === ",") {
      trailingComma = true;
      continue;
    }

    // TODO: Handle incomplete non-string values (like "tr" for true)
    if (!/\s/.test(chr)) {
      // This is a value
      if (needValue) {
        needValue = false;
      }
    }
  }

  let fixedJson = buildNewJsonString;
  let jsonComplete = true;

  // Fix the JSON in order:

  // If the JSON ends in a / it is escaping something that isn't there yet, remove the /
  if (fixedJson.endsWith("\\")) {
    fixedJson = fixedJson.slice(0, -1);
    jsonComplete = false;
  }

  // If inside a string, close the string first
  if (insideString) {
    fixedJson += '"';
    jsonComplete = false;
  }

  // If trailing comma, remove it
  if (trailingComma) {
    const commaIndex = fixedJson.lastIndexOf(",");
    fixedJson = fixedJson.slice(0, commaIndex);
    jsonComplete = false;
  }

  if (needColon) {
    fixedJson += ": ";
    jsonComplete = false;
  }

  if (needValue) {
    fixedJson += '""';
    jsonComplete = false;
  }

  unclosedSymbols.reverse().forEach((chr) => {
    if (chr === "{") {
      fixedJson += "}";
    }

    if (chr === "[") {
      fixedJson += "]";
    }

    jsonComplete = false;
  });

  try {
    const fixedObj = JSON.parse(fixedJson);
    return {
      fixedStr: fixedJson,
      fixedObj,
      complete: jsonComplete,
      broken: false,
    };
  } catch (e) {
    if (verbose) {
      console.info(fixedJson);
      console.info(e);
    }
    return {
      fixedStr: "{}",
      fixedObj: {},
      complete: false,
      broken: true,
    };
  }
};

export const getEditorLanguageForFilename = (filename: string) => {
  let language = "text";
  if (filename) {
    if (filename.endsWith("css")) {
      language = "css";
    } else if (
      filename.endsWith("js") ||
      filename.endsWith("jsx") ||
      filename.endsWith("mjs") ||
      filename.endsWith("cjs")
    ) {
      language = "javascript";
    } else if (
      filename.endsWith("tsx") ||
      filename.endsWith("ts") ||
      filename.endsWith("mts") ||
      filename.endsWith("cts")
    ) {
      language = "javascript";
    } else if (filename.endsWith("json")) {
      language = "json";
    } else if (filename.endsWith("md")) {
      language = "markdown";
    } else if (filename.endsWith("html")) {
      language = "html";
    } else if (filename.endsWith("xml")) {
      language = "xml";
    } else if (filename.endsWith("yaml") || filename.endsWith("yml")) {
      language = "yaml";
    } else if (filename.endsWith("sh")) {
      language = "shell";
    } else {
      language = "text";
    }
  }

  return language;
};

export const setLocalStorageCompressed = (key: string, obj: any) => {
  localStorage.setItem(
    key,
    uint8ArrayToBase64(pako.deflate(JSON.stringify(obj), { to: "string" })),
  );
};

export const getLocalStorageCompressed = (key: string) => {
  const localStorageValue = localStorage.getItem(key);

  if (!localStorageValue) {
    return;
  }

  let parsedObj;
  try {
    const possibleObjStr = pako.inflate(base64ToUint8Array(localStorageValue), {
      to: "string",
    });

    parsedObj = JSON.parse(possibleObjStr);
  } catch (e) {
    console.error("Error parsing compressed object for key: ", key);
    console.error(e);

    // Ledger was not compressed before
    try {
      parsedObj = JSON.parse(localStorageValue);
    } catch (e) {
      console.error("Error parsing uncompressed object for key: ", key);
      console.error(e);
      return;
    }
  }

  return parsedObj;
};

// Function to convert a Uint8Array to a Base64 string
export const uint8ArrayToBase64 = (uint8Array) => {
  return btoa(String.fromCharCode(...uint8Array));
};

// Function to convert a Base64 string to a Uint8Array
export const base64ToUint8Array = (base64) => {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

export const replaceNoWhitespace = (
  str: string,
  find: string,
  replace: string,
): string => {
  if (!find) {
    // @ts-expect-error TS2322
    return;
  }

  // Strip whitespace from the input strings
  const strippedStr = str.replace(/\s+/g, "");
  const strippedFind = find.replace(/\s+/g, "");

  // Now iterate through the original string and stripped string to find the place to make the replacement
  let strippedIndex = 0;
  let originalIndex = 0;

  // When inside a particular replacement
  let currentOriginalStart: any = null;
  let currentStrippedEndSearch: any = null;

  let allReplacementIndicies: { start: number; end: number }[] = []; // (incl, incl)
  while (originalIndex < str.length) {
    if (!/\s/.test(str[originalIndex])) {
      // This is the end index, add to replacement indicies
      if (
        currentStrippedEndSearch &&
        currentStrippedEndSearch === strippedIndex
      ) {
        // Insert into the front because as you modify the new string the index changes
        allReplacementIndicies = [
          {
            start: currentOriginalStart,
            end: originalIndex,
          },
        ].concat(allReplacementIndicies);

        currentOriginalStart = null;
        currentStrippedEndSearch = null;
      } else if (strippedStr.startsWith(strippedFind, strippedIndex)) {
        // Start search for end
        currentOriginalStart = originalIndex;
        currentStrippedEndSearch = strippedIndex + strippedFind.length - 1;
      }

      strippedIndex++;
    }

    originalIndex++;
  }

  // Modify the string now
  let newStr = str;
  allReplacementIndicies.forEach((indicies) => {
    newStr =
      newStr.slice(0, indicies.start) +
      replace +
      newStr.slice(indicies.end + 1); // +1 on the end because the end is inclusive
  });

  return newStr;
};

export const logAxiosError = (msg: string, err: unknown): void => {
  console.error(err);

  // Call the original console.error so it doesn't track multiple times in posthog
  originalConsoleError(err);
  originalConsoleError("Error constructor:", err?.constructor?.name);
  originalConsoleError("Error prototype:", Object.getPrototypeOf(err));

  // Only capture actual Error instances in Sentry
  if (err instanceof Error) {
    Sentry.captureException(err);
  }

  // If this is an error thrown by the axios library, log the error, along with the body if it is a 4xx error
  if (axios.isAxiosError(err)) {
    originalConsoleError("=== Error Details ===");
    originalConsoleError("Message:", msg);
    originalConsoleError("Error Message:", err.message);

    // Request details
    originalConsoleError("\n=== Request Details ===");
    originalConsoleError("Method:", err.config?.method?.toUpperCase());
    originalConsoleError("URL:", err.config?.url);
    originalConsoleError("Request Data:", err.config?.data);

    // Response details (if available)
    if (err.response) {
      originalConsoleError("\n=== Response Details ===");
      originalConsoleError("Status:", err.response.status);
      originalConsoleError("Status Text:", err.response.statusText);
      originalConsoleError("Response Headers:", err.response.headers);
      originalConsoleError("Response Data:", err.response.data);
    } else {
      originalConsoleError("\n=== Network Error ===");
      originalConsoleError("No response received - likely a network error");
    }

    // Stack trace
    originalConsoleError("\n=== Stack Trace ===");
    originalConsoleError(err.stack);

    return;
  }

  // For Error instances
  if (err instanceof Error) {
    originalConsoleError("=== Non-Axios Error ===");
    originalConsoleError("Message:", msg);
    originalConsoleError("Error Type:", err.constructor.name);
    originalConsoleError("Error Message:", err.message);
    originalConsoleError("Stack Trace:", err.stack);
    return;
  }

  // For any other type of error
  originalConsoleError("=== Unknown Error Type ===");
  originalConsoleError("Message:", msg);
  originalConsoleError("Error Value:", err);
  originalConsoleError("Error Type:", typeof err);
};

export const getAppliedStatusIconForIssues = (
  issueIds: string[],
  value: string | null,
  filter: (issue: Issue, issueValue: string | null) => boolean,
) => {
  const issueStoreState = useIssuesStore.getState();
  const issueIdsToCheck = issueIds
    .map((issueId) => issueStoreState.canvasIdToIssue[issueId])
    .filter((issue) => issue && filter(issue, value));

  let appliedStatus = SubMenuApplicationStatus.NONE;
  if (issueIdsToCheck.length === issueIds.length && issueIds.length > 0) {
    appliedStatus = SubMenuApplicationStatus.ALL;
  } else if (issueIdsToCheck.length === 0) {
    appliedStatus = SubMenuApplicationStatus.NONE;
  } else {
    appliedStatus = SubMenuApplicationStatus.PARTIAL;
  }

  switch (appliedStatus) {
    case SubMenuApplicationStatus.NONE:
      return null;
    case SubMenuApplicationStatus.PARTIAL:
      return React.createElement(MinusIcon, { className: "size-4 text-white" });
    case SubMenuApplicationStatus.ALL:
      return React.createElement(CheckIcon, { className: "size-4 text-white" });
    default:
      return null;
  }
};

export const useCanvasId = (): string => {
  const params = useParams<{ canvasId?: string }>();
  // Fall back to masterCanvasId from pmAgentStore (for PM Agent on projects page)
  const masterCanvasId = usePMAgentStore((state) => state.masterCanvasId);
  return params?.canvasId ?? masterCanvasId ?? "";
};

export const useProjectId = (): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("project_id");
};

export const useSelectedPageId = (): string | null => {
  const canvasId = useCanvasId();
  return usePagesStore((state) => state.selectedPageId[canvasId]);
};

export const useIsGithubConnected = (): boolean => {
  const { user } = useUser();

  const githubConnection = user?.externalAccounts.find(
    (account) =>
      account.provider === "github" &&
      !account.verification?.error &&
      account.verification?.status === "verified",
  );

  return Boolean(githubConnection);
};

export const useIsGitlabConnected = (): boolean => {
  const { user } = useUser();

  const gitlabConnection = user?.externalAccounts.find(
    (account) =>
      account.provider === "gitlab" &&
      !account.verification?.error &&
      account.verification?.status === "verified",
  );

  return Boolean(gitlabConnection);
};

export const useIsBitbucketConnected = (): boolean => {
  const { user } = useUser();

  const bitbucketConnection = user?.externalAccounts.find(
    (account) =>
      account.provider === "bitbucket" &&
      !account.verification?.error &&
      account.verification?.status === "verified",
  );

  return Boolean(bitbucketConnection);
};

export const getUnexpiredToken = async (
  session: SignedInSessionResource | null | undefined,
  options?: GetTokenOptions,
): Promise<string | null> => {
  if (!session) {
    return null;
  }

  const token = await session.getToken(options);

  if (!token) {
    return await session.getToken({ skipCache: true, ...options });
  }

  // Decode the JWT to check expiration
  const payload = JSON.parse(atob(token.split(".")[1]));
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;

  if (timeUntilExpiration >= 15000) {
    return token;
  }

  // If token expires in less than 15 seconds, refresh it
  const refreshedToken = await session.getToken({ skipCache: true });
  if (!refreshedToken) {
    return null;
  }

  // Check expiration and log if it's expired
  const refreshedPayload = JSON.parse(atob(refreshedToken.split(".")[1]));
  const refreshedExpirationTime = refreshedPayload.exp * 1000;
  const refreshedCurrentTime = Date.now();
  const refreshedTimeUntilExpiration =
    refreshedExpirationTime - refreshedCurrentTime;

  if (refreshedTimeUntilExpiration <= 0) {
    console.error(
      `Newly minted token is expired: ${refreshedTimeUntilExpiration}`,
    );
  }

  return refreshedToken;
};

export const useDebugEffect = (
  callback: Function,
  dependencies: any[],
  effectName?: string,
) => {
  const prevDeps = useRef(dependencies);

  useEffect(() => {
    const changedDeps = dependencies
      .map((dep, i) => {
        if (dep !== prevDeps.current[i]) {
          return {
            index: i,
            from: prevDeps.current[i],
            to: dep,
          };
        }
        return null;
      })
      .filter(Boolean);

    const logChangeFunc = () => {
      if (changedDeps.length > 0) {
        console.log(`[${effectName || "Effect"}] Dependencies changed:`);
        changedDeps.forEach((change) => {
          console.log(change);
        });
      }
    };

    prevDeps.current = dependencies;
    return callback(logChangeFunc);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};

export const withTimeout = (fn: () => Promise<any>, timeout: number) => {
  return Promise.race([
    fn(),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timeout")), timeout),
    ),
  ]);
};

/**
 * Converts a full file path to a relative path by removing the canvasId prefix.
 * This is commonly used when displaying paths to users or in AI prompts.
 *
 * @param fullPath - The full file path (e.g., "/Users/name/canvas123/src/components/Button.tsx")
 * @param canvasId - The canvas ID to split on (e.g., "canvas123")
 * @returns The relative path without leading slashes (e.g., "src/components/Button.tsx")
 *
 * @example
 * getRelativePathFromCanvas("/Users/name/canvas123/src/components/Button.tsx", "canvas123")
 * // Returns: "src/components/Button.tsx"
 */
export const getRelativePathFromCanvas = (
  fullPath: string,
  canvasId: string,
): string => {
  if (!fullPath || !canvasId) {
    return fullPath;
  }

  // Split the path on canvasId and take the part after it
  const parts = fullPath.split(canvasId);
  if (parts.length > 1) {
    // Remove leading slashes if present
    return parts[1].replace(/^\/+/, "");
  }

  return fullPath;
};

export function isValidURL(str: string): boolean {
  try {
    const trimmed = str.trim();
    if (!trimmed) return false;

    // Try to create a URL object
    new URL(trimmed);
    return true;
  } catch {
    // If URL constructor throws, it's not a valid URL
    return false;
  }
}

export const useInitialRenderEffect = (callback: Function) => {
  return useEffect(() => {
    callback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useInitialRenderCallback = <T extends Function>(fns: T): T => {
  return useCallback(fns, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useAutoUpdateRef = <T,>(value: T): React.MutableRefObject<T> => {
  const ref = useRef(value);
  // We make the explicit decision not to use a use effect
  // so that this ref is always up to date in a render loop
  ref.current = value;
  return ref;
};

// Utility function to safely validate and construct repository URLs
export const getSafeRepositoryUrl = (
  provider: string,
  repoPath: string,
): string | null => {
  if (!repoPath || !PRD_CONSTANTS.VALID_REPO_PATH_REGEX.test(repoPath)) {
    console.warn("Invalid repository path format:", repoPath);
    return null;
  }

  const sanitizedRepoPath = repoPath.replace(/[^a-zA-Z0-9._/-]/g, "");

  switch (provider) {
    case PRD_CONSTANTS.REPOSITORY_PROVIDERS.GITHUB:
      return `${PRD_CONSTANTS.REPOSITORY_URLS.GITHUB}${sanitizedRepoPath}`;
    case PRD_CONSTANTS.REPOSITORY_PROVIDERS.GITLAB:
      return `${PRD_CONSTANTS.REPOSITORY_URLS.GITLAB}${sanitizedRepoPath}`;
    case PRD_CONSTANTS.REPOSITORY_PROVIDERS.BITBUCKET:
      return `${PRD_CONSTANTS.REPOSITORY_URLS.BITBUCKET}${sanitizedRepoPath}`;
    default:
      return `${PRD_CONSTANTS.REPOSITORY_URLS.GITHUB}${sanitizedRepoPath}`;
  }
};
